import api from "app/integration/api";
import { cleanBarcode } from "app/common/cleanBarcode";

/**
 * Mencari dan merekonsiliasi data produk beserta variannya berdasarkan barcode yang dipindai.
 * Melakukan resolusi berlapis:
 * 1. Memeriksa memori lokal (localVariants) jika tersedia.
 * 2. Fallback query ke API productVariant berdasarkan qr_code.
 * 3. Fallback query ke API productCode (barcode unik unit fisik) untuk mendapatkan product_variant_id.
 * 4. Fallback query ke API product berdasarkan kode/SKU produk.
 *
 * @param {string} barcode - Barcode mentah hasil pembacaan pemindai.
 * @param {Array} [localVariants=[]] - Daftar opsi varian produk yang ada di state lokal/collector.
 * @returns {Promise<Object|null>} Objek produk terformat untuk handleSuccessProductChoose, atau null jika tidak ditemukan.
 * @sideEffects Melakukan request network ke API backend jika produk tidak ditemukan di cache lokal.
 */
export async function resolveProductBarcode(barcode, localVariantsOrOptions = []) {
  const clean = cleanBarcode(barcode);
  if (!clean) return null;

  const localVariants = Array.isArray(localVariantsOrOptions)
    ? localVariantsOrOptions
    : localVariantsOrOptions?.localVariants || [];

  let variant = null;
  let baseProduct = null;

  // 1. Cek di daftar varian memori lokal jika disediakan
  if (Array.isArray(localVariants) && localVariants.length > 0) {
    variant = localVariants.find(
      (item) =>
        cleanBarcode(item?.qr_code) === clean ||
        item?.qr_code === clean
    );
  }

  // 2. Fallback: Query ke API productVariant berdasarkan qr_code
  if (!variant) {
    try {
      const res = await api.productVariant.index({ qr_code: clean });
      const items = Array.isArray(res?.data)
        ? res.data
        : res?.data?.items || [];
      if (items.length > 0) {
        variant = items[0];
      }
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      console.warn("[resolveProductBarcode] Gagal mencari productVariant:", err);
    }
  }

  // 3. Fallback: Query ke API productCode (kode fisik unit/serial)
  if (!variant) {
    try {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clean);
      let res = await api.productCode.index(
        isUuid ? { qr_code: clean, limit: 1 } : { ref: clean, limit: 1 }
      );
      let items = Array.isArray(res?.data)
        ? res.data
        : res?.data?.items || [];

      if (items.length === 0 && isUuid) {
        try {
          const fallbackRes = await api.productCode.index({ ref: clean, limit: 1 });
          items = Array.isArray(fallbackRes?.data)
            ? fallbackRes.data
            : fallbackRes?.data?.items || [];
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }
      }

      if (items.length > 0) {
        const productCode = items[0];
        const variantId =
          productCode.product_variant_id ||
          productCode.product_variant?.id;

        if (variantId) {
          const varRes = await api.productVariant.show(variantId);
          const varData = varRes?.data?.data || varRes?.data;
          if (varData) {
            variant = varData;
          }
        }
      }
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      console.warn("[resolveProductBarcode] Gagal mencari productCode:", err);
    }
  }

  // 4. Fallback: Query ke API product berdasarkan code / SKU produk
  if (!variant) {
    try {
      const res = await api.product.index({ code: clean });
      const items = Array.isArray(res?.data)
        ? res.data
        : res?.data?.items || [];
      if (items.length > 0) {
        baseProduct = items[0];
        if (
          baseProduct.ProductVariants &&
          baseProduct.ProductVariants.length > 0
        ) {
          variant = baseProduct.ProductVariants[0];
        }
      }
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      console.warn("[resolveProductBarcode] Gagal mencari product:", err);
    }
  }

  // Jika tidak ditemukan di semua tingkatan
  if (!variant && !baseProduct) {
    return null;
  }

  // Pastikan detail produk terlengkapi
  const productId = variant?.product_id || baseProduct?.id;
  let productDetail = variant?.product_detail || variant?.product || baseProduct;

  if ((!productDetail || !productDetail.ProductVariants || productDetail.ProductVariants.length === 0) && productId) {
    try {
      const prodRes = await api.product.show(productId);
      const fetched = prodRes?.data?.data || prodRes?.data;
      if (fetched) {
        productDetail = fetched;
      }
    } catch (err) {
      console.warn("[resolveProductBarcode] Gagal mengambil detail produk:", err);
    }
  }

  // Pastikan detail ukuran (size) terlengkapi
  let sizeDetail = variant?.size;
  if (!sizeDetail && variant?.size_variant_id) {
    try {
      const sizeRes = await api.sizeVariant.show(variant.size_variant_id);
      sizeDetail = sizeRes?.data;
    } catch (err) {
      console.warn("[resolveProductBarcode] Gagal mengambil detail size:", err);
    }
  }

  // Pastikan detail warna (color) terlengkapi
  let colorDetail = variant?.color;
  if (!colorDetail && variant?.color_variant_id) {
    try {
      const colorRes = await api.colorVariant.show(variant.color_variant_id);
      colorDetail = colorRes?.data;
    } catch (err) {
      console.warn("[resolveProductBarcode] Gagal mengambil detail color:", err);
    }
  }

  let productVariantsList = Array.isArray(productDetail?.ProductVariants) && productDetail.ProductVariants.length > 0
    ? productDetail.ProductVariants
    : Array.isArray(baseProduct?.ProductVariants) && baseProduct.ProductVariants.length > 0
    ? baseProduct.ProductVariants
    : [];

  if (productVariantsList.length === 0 && variant) {
    productVariantsList = [
      {
        id: variant.id,
        size: {
          id: variant?.size_variant_id || sizeDetail?.id || 0,
          name: sizeDetail?.name || "All Size",
        },
        color: {
          id: variant?.color_variant_id || colorDetail?.id || 0,
          name: colorDetail?.name || "All Color",
        },
      },
    ];
  } else {
    productVariantsList = productVariantsList.map((pv) => ({
      ...pv,
      size: pv.size && typeof pv.size === "object" ? pv.size : {
        id: pv.size_variant_id || 0,
        name: pv.size || "All Size",
      },
      color: pv.color && typeof pv.color === "object" ? pv.color : {
        id: pv.color_variant_id || 0,
        name: pv.color || "All Color",
      },
    }));
  }

  return {
    product: {
      ...(productDetail || {}),
      id: productId,
      name: productDetail?.name || variant?.product_detail?.name || baseProduct?.name || "Produk",
      code: productDetail?.code || variant?.product_detail?.code || baseProduct?.code || "",
      is_unique_barcode:
        variant?.product_detail?.is_unique_barcode !== undefined
          ? variant.product_detail.is_unique_barcode
          : productDetail?.is_unique_barcode,
      price:
        variant?.product_detail?.price ||
        productDetail?.price ||
        0,
      weight:
        variant?.product_detail?.weight ||
        productDetail?.weight ||
        0,
      imageUrl:
        variant?.product_detail?.imageUrl ||
        productDetail?.imageUrl ||
        "",
      ProductVariants: productVariantsList,
    },
    variant: {
      id: variant?.id || null,
      qr_code: variant?.qr_code || "",
      product_id: productId,
      size: {
        id: variant?.size_variant_id || (typeof sizeDetail === "object" ? sizeDetail?.id : 0) || 0,
        name: (typeof sizeDetail === "object" ? sizeDetail?.name : sizeDetail) || "All Size",
      },
      color: {
        id: variant?.color_variant_id || (typeof colorDetail === "object" ? colorDetail?.id : 0) || 0,
        name: (typeof colorDetail === "object" ? colorDetail?.name : colorDetail) || "All Color",
      },
    },
    rawVariant: variant,
  };
}

export default resolveProductBarcode;
